// =====================================================
// ADMIN INFO MODAL
//
// Composant réservé aux administrateurs (admin / adminpro / supreme)
// pour gérer les entrées de la table `info`.
//
// FONCTIONNALITÉS :
//   - Lister toutes les entrées info
//   - Créer une nouvelle entrée
//   - Modifier une entrée existante
//   - Supprimer une entrée (confirmation requise)
//
// USAGE dans profile.tsx :
//   <AdminInfoModal
//     visible={showAdminInfo}
//     userId={userId}
//     accessToken={accessTokenRef.current}
//     onClose={() => setShowAdminInfo(false)}
//   />
// =====================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Dimensions,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons }      from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import * as Haptics      from 'expo-haptics';

// ─── Config ──────────────────────────────────────────────────────────────────
const WS_BASE  = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const INFO_URL = `${WS_BASE}/info`;

const { width: SCREEN_W, height: SCREEN_H } = Dimensions.get('window');
const NATIVE = Platform.OS !== 'web';

// ─── Palette ─────────────────────────────────────────────────────────────────
const C = {
  bg:       '#0E0017',
  surface:  '#160020',
  card:     '#1C0030',
  border:   '#2E1050',
  purple:   '#8A2BE2',
  purpleDk: '#4B0082',
  gold:     '#FFD700',
  red:      '#EF4444',
  green:    '#10B981',
  text:     '#F5F0FF',
  muted:    '#9B82C4',
};

// ─── Types ───────────────────────────────────────────────────────────────────
interface InfoEntry {
  id:         string;
  titre:      string;
  texte:      string;
  created_at: string;
  updated_at: string;
}

interface AdminInfoModalProps {
  visible:     boolean;
  userId:      string;
  accessToken: string;
  onClose:     () => void;
}

type ViewMode = 'list' | 'form';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', {
    day:    'numeric',
    month:  'long',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
  });

// ─── Composant ───────────────────────────────────────────────────────────────
export default function AdminInfoModal({
  visible,
  userId,
  accessToken,
  onClose,
}: AdminInfoModalProps) {

  const [mode,        setMode]        = useState<ViewMode>('list');
  const [infos,       setInfos]       = useState<InfoEntry[]>([]);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [deleting,    setDeleting]    = useState<string | null>(null);
  const [confirmDel,  setConfirmDel]  = useState<InfoEntry | null>(null);
  const [editTarget,  setEditTarget]  = useState<InfoEntry | null>(null);

  // Champs du formulaire
  const [titre, setTitre] = useState('');
  const [texte, setTexte] = useState('');

  // Toast
  const [toast,      setToast]      = useState<{ type: 'ok' | 'err'; msg: string } | null>(null);
  const toastAnim = useRef(new Animated.Value(0)).current;

  // ── Toast helper ───────────────────────────────────────────────────────────
  const showToast = (type: 'ok' | 'err', msg: string) => {
    setToast({ type, msg });
    Animated.sequence([
      Animated.timing(toastAnim, { toValue: 1, duration: 200, useNativeDriver: true }),
      Animated.delay(2800),
      Animated.timing(toastAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
    ]).start(() => setToast(null));
  };

  // ── Fetch toutes les infos ─────────────────────────────────────────────────
  const loadInfos = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(INFO_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          action:       'get-all-info',
          access_token: accessToken,
        }),
      });
      const data = await res.json();
      if (data.success) setInfos(data.infos || []);
      else showToast('err', data.error || 'Erreur de chargement');
    } catch {
      showToast('err', 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (visible) { loadInfos(); }
  }, [visible]);

  // ── Ouvrir formulaire ──────────────────────────────────────────────────────
  const openCreate = () => {
    setEditTarget(null);
    setTitre('');
    setTexte('');
    setMode('form');
  };

  const openEdit = (entry: InfoEntry) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setEditTarget(entry);
    setTitre(entry.titre);
    setTexte(entry.texte);
    setMode('form');
  };

  // ── Sauvegarder (create ou update) ────────────────────────────────────────
  const handleSave = async () => {
    if (!titre.trim()) { showToast('err', 'Le titre est obligatoire'); return; }
    if (!texte.trim()) { showToast('err', 'Le texte est obligatoire');  return; }
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);

    setSaving(true);
    try {
      const action = editTarget ? 'update-info' : 'create-info';
      const body: any = {
        action,
        access_token: accessToken,
        titre:        titre.trim(),
        texte:        texte.trim(),
      };
      if (editTarget) body.id = editTarget.id;

      const res  = await fetch(INFO_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();

      if (data.success) {
        showToast('ok', editTarget ? 'Information modifiée !' : 'Information créée !');
        setMode('list');
        await loadInfos();
      } else {
        showToast('err', data.error || 'Impossible de sauvegarder');
      }
    } catch {
      showToast('err', 'Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  // ── Supprimer ──────────────────────────────────────────────────────────────
  const handleDelete = async (entry: InfoEntry) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
    setDeleting(entry.id);
    try {
      const res  = await fetch(INFO_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          action:       'delete-info',
          access_token: accessToken,
          id:           entry.id,
        }),
      });
      const data = await res.json();

      if (data.success) {
        showToast('ok', 'Information supprimée');
        setConfirmDel(null);
        await loadInfos();
      } else {
        showToast('err', data.error || 'Impossible de supprimer');
      }
    } catch {
      showToast('err', 'Erreur réseau');
    } finally {
      setDeleting(null);
    }
  };

  // ── Rendu ──────────────────────────────────────────────────────────────────
  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        style={styles.root}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        {/* ── GRADIENT HEADER ── */}
        <LinearGradient
          colors={[C.purpleDk, C.purple]}
          style={styles.header}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        >
          {/* Orbes */}
          <View style={[styles.orb, { top: -40, right: -30, width: 120, height: 120, backgroundColor: C.gold, opacity: 0.10 }]} />
          <View style={[styles.orb, { bottom: -20, left: 10,  width: 70,  height: 70,  backgroundColor: '#FF8C00', opacity: 0.12 }]} />

          <View style={styles.headerRow}>
            {/* Bouton retour / fermer */}
            <TouchableOpacity
              style={styles.headerBtn}
              onPress={mode === 'form' ? () => setMode('list') : onClose}
            >
              <Ionicons
                name={mode === 'form' ? 'arrow-back' : 'close'}
                size={22}
                color="rgba(255,255,255,0.9)"
              />
            </TouchableOpacity>

            <View style={styles.headerCenter}>
              <View style={styles.adminBadge}>
                <Ionicons name="shield-checkmark" size={12} color={C.gold} />
                <Text style={styles.adminBadgeText}>ADMINISTRATION</Text>
              </View>
              <Text style={styles.headerTitle}>
                {mode === 'list'
                  ? 'Gestion des Informations'
                  : editTarget ? 'Modifier l\'information' : 'Nouvelle information'}
              </Text>
            </View>

            {/* Bouton créer (vue liste uniquement) */}
            {mode === 'list' ? (
              <TouchableOpacity style={styles.createBtn} onPress={openCreate}>
                <Ionicons name="add" size={22} color="#FFFFFF" />
              </TouchableOpacity>
            ) : (
              <View style={{ width: 40 }} />
            )}
          </View>
        </LinearGradient>

        {/* ── TOAST ── */}
        {toast && (
          <Animated.View
            style={[
              styles.toast,
              toast.type === 'ok' ? styles.toastOk : styles.toastErr,
              { opacity: toastAnim },
            ]}
          >
            <Ionicons
              name={toast.type === 'ok' ? 'checkmark-circle' : 'close-circle'}
              size={18}
              color="#fff"
            />
            <Text style={styles.toastText}>{toast.msg}</Text>
          </Animated.View>
        )}

        {/* ────────────────────────────────────────────────────────────── */}
        {/*  VUE LISTE                                                     */}
        {/* ────────────────────────────────────────────────────────────── */}
        {mode === 'list' && (
          <ScrollView
            style={styles.body}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Compteur */}
            <View style={styles.countRow}>
              <Ionicons name="information-circle-outline" size={14} color={C.muted} />
              <Text style={styles.countText}>
                {infos.length} information{infos.length !== 1 ? 's' : ''} en base
              </Text>
            </View>

            {loading ? (
              <View style={styles.centered}>
                <ActivityIndicator size="large" color={C.purple} />
                <Text style={styles.loadingText}>Chargement…</Text>
              </View>
            ) : infos.length === 0 ? (
              <View style={styles.empty}>
                <Ionicons name="document-outline" size={56} color={C.border} />
                <Text style={styles.emptyTitle}>Aucune information</Text>
                <Text style={styles.emptySubtitle}>
                  Appuyez sur + pour créer le premier message du bandeau.
                </Text>
                <TouchableOpacity style={styles.emptyCreateBtn} onPress={openCreate}>
                  <Ionicons name="add-circle-outline" size={18} color={C.purple} />
                  <Text style={styles.emptyCreateText}>Créer une information</Text>
                </TouchableOpacity>
              </View>
            ) : (
              infos.map((entry) => (
                <InfoCard
                  key={entry.id}
                  entry={entry}
                  deleting={deleting === entry.id}
                  onEdit={() => openEdit(entry)}
                  onDelete={() => setConfirmDel(entry)}
                />
              ))
            )}
          </ScrollView>
        )}

        {/* ────────────────────────────────────────────────────────────── */}
        {/*  VUE FORMULAIRE                                                */}
        {/* ────────────────────────────────────────────────────────────── */}
        {mode === 'form' && (
          <ScrollView
            style={styles.body}
            contentContainerStyle={styles.formContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Champ Titre */}
            <Text style={styles.fieldLabel}>Titre du bandeau</Text>
            <Text style={styles.fieldHint}>
              Affiché dans la pill à gauche du bandeau (max 30 caractères recommandés)
            </Text>
            <View style={styles.inputWrap}>
              <Ionicons name="text-outline" size={18} color={C.muted} style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                value={titre}
                onChangeText={setTitre}
                placeholder="ex : 🏆 Harmonia — Centre des Talents"
                placeholderTextColor={C.muted + '80'}
                maxLength={80}
                autoCapitalize="sentences"
              />
            </View>
            <Text style={styles.charCount}>{titre.length} / 80</Text>

            {/* Champ Texte */}
            <Text style={[styles.fieldLabel, { marginTop: 20 }]}>Texte défilant</Text>
            <Text style={styles.fieldHint}>
              Défile horizontalement en boucle dans le bandeau.
              Plus le texte est long, plus le défilement est lent et fluide.
            </Text>
            <View style={[styles.inputWrap, styles.inputWrapMulti]}>
              <TextInput
                style={[styles.input, styles.inputMulti]}
                value={texte}
                onChangeText={setTexte}
                placeholder="ex : Bienvenue sur Harmonia ! Participez aux défis, montrez votre talent et remportez des trophées remis publiquement…"
                placeholderTextColor={C.muted + '80'}
                multiline
                maxLength={1000}
                textAlignVertical="top"
                autoCapitalize="sentences"
              />
            </View>
            <Text style={styles.charCount}>{texte.length} / 1000</Text>

            {/* Aperçu */}
            {(titre || texte) && (
              <View style={styles.previewWrap}>
                <View style={styles.previewLabel}>
                  <Ionicons name="eye-outline" size={13} color={C.muted} />
                  <Text style={styles.previewLabelText}>Aperçu du bandeau</Text>
                </View>
                <View style={styles.previewBanner}>
                  <View style={styles.previewPill}>
                    <Ionicons name="megaphone-outline" size={10} color={C.gold} />
                    <Text style={styles.previewPillText} numberOfLines={1}>
                      {titre.length > 16 ? titre.slice(0, 16) + '…' : titre || '—'}
                    </Text>
                  </View>
                  <View style={styles.previewSep} />
                  <Text style={styles.previewTickerText} numberOfLines={1}>
                    {texte || '—'}
                  </Text>
                </View>
              </View>
            )}

            {/* Bouton sauvegarder */}
            <TouchableOpacity
              style={[
                styles.saveBtn,
                (!titre.trim() || !texte.trim()) && styles.saveBtnDisabled,
              ]}
              onPress={handleSave}
              disabled={saving || !titre.trim() || !texte.trim()}
              activeOpacity={0.85}
            >
              <LinearGradient
                colors={[C.purpleDk, C.purple]}
                style={styles.saveBtnGrad}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
              >
                {saving ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <>
                    <Ionicons
                      name={editTarget ? 'checkmark-circle-outline' : 'add-circle-outline'}
                      size={18}
                      color="#fff"
                    />
                    <Text style={styles.saveBtnText}>
                      {editTarget ? 'Enregistrer les modifications' : 'Publier l\'information'}
                    </Text>
                  </>
                )}
              </LinearGradient>
            </TouchableOpacity>
          </ScrollView>
        )}

        {/* ────────────────────────────────────────────────────────────── */}
        {/*  MODAL CONFIRMATION SUPPRESSION                                */}
        {/* ────────────────────────────────────────────────────────────── */}
        <Modal
          visible={!!confirmDel}
          transparent
          animationType="fade"
          onRequestClose={() => setConfirmDel(null)}
        >
          <View style={styles.confirmOverlay}>
            <View style={styles.confirmCard}>
              {/* Icône */}
              <View style={styles.confirmIconWrap}>
                <Ionicons name="trash-outline" size={32} color={C.red} />
              </View>

              <Text style={styles.confirmTitle}>Supprimer cette information ?</Text>
              <Text style={styles.confirmTitre} numberOfLines={2}>
                « {confirmDel?.titre} »
              </Text>
              <Text style={styles.confirmMsg}>
                Cette action est définitive. Le bandeau ne sera plus affiché
                aux utilisateurs jusqu'à la création d'une nouvelle information.
              </Text>

              <View style={styles.confirmBtns}>
                <TouchableOpacity
                  style={styles.confirmCancelBtn}
                  onPress={() => setConfirmDel(null)}
                >
                  <Text style={styles.confirmCancelText}>Annuler</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.confirmDeleteBtn}
                  onPress={() => confirmDel && handleDelete(confirmDel)}
                  disabled={!!deleting}
                >
                  {deleting ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <Text style={styles.confirmDeleteText}>Supprimer</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

      </KeyboardAvoidingView>
    </Modal>
  );
}

// ─── InfoCard ─────────────────────────────────────────────────────────────────
function InfoCard({
  entry,
  deleting,
  onEdit,
  onDelete,
}: {
  entry:    InfoEntry;
  deleting: boolean;
  onEdit:   () => void;
  onDelete: () => void;
}) {
  return (
    <View style={cardStyles.card}>
      {/* Bande colorée gauche */}
      <View style={cardStyles.stripe} />

      <View style={cardStyles.body}>
        {/* Titre */}
        <View style={cardStyles.titleRow}>
          <View style={cardStyles.titlePill}>
            <Ionicons name="megaphone-outline" size={11} color={C.gold} />
            <Text style={cardStyles.titlePillText} numberOfLines={1}>
              {entry.titre}
            </Text>
          </View>
        </View>

        {/* Texte */}
        <Text style={cardStyles.texte} numberOfLines={3}>
          {entry.texte}
        </Text>

        {/* Dates */}
        <View style={cardStyles.datesRow}>
          <Ionicons name="time-outline" size={11} color={C.muted} />
          <Text style={cardStyles.dateText}>
            Créé le {formatDate(entry.created_at)}
          </Text>
        </View>
        {entry.updated_at !== entry.created_at && (
          <View style={cardStyles.datesRow}>
            <Ionicons name="pencil-outline" size={11} color={C.muted} />
            <Text style={cardStyles.dateText}>
              Modifié le {formatDate(entry.updated_at)}
            </Text>
          </View>
        )}

        {/* Actions */}
        <View style={cardStyles.actions}>
          <TouchableOpacity style={cardStyles.editBtn} onPress={onEdit}>
            <Ionicons name="pencil-outline" size={15} color={C.purple} />
            <Text style={cardStyles.editText}>Modifier</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={cardStyles.deleteBtn}
            onPress={onDelete}
            disabled={deleting}
          >
            {deleting ? (
              <ActivityIndicator size="small" color={C.red} />
            ) : (
              <>
                <Ionicons name="trash-outline" size={15} color={C.red} />
                <Text style={cardStyles.deleteText}>Supprimer</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: {
    flex:            1,
    backgroundColor: C.bg,
  },

  // ── Header ──
  header: {
    paddingTop:    Platform.OS === 'ios' ? 20 : 16,
    paddingBottom: 20,
    overflow:      'hidden',
  },
  orb: {
    position:     'absolute',
    borderRadius: 9999,
  },
  headerRow: {
    flexDirection:  'row',
    alignItems:     'center',
    paddingHorizontal: 16,
    gap: 10,
  },
  headerBtn: {
    width:           40,
    height:          40,
    borderRadius:    12,
    backgroundColor: 'rgba(0,0,0,0.25)',
    justifyContent:  'center',
    alignItems:      'center',
  },
  headerCenter: {
    flex:      1,
    alignItems: 'center',
    gap:        4,
  },
  adminBadge: {
    flexDirection:     'row',
    alignItems:        'center',
    gap:               5,
    backgroundColor:   'rgba(255,215,0,0.15)',
    borderWidth:       1,
    borderColor:       'rgba(255,215,0,0.30)',
    paddingHorizontal: 10,
    paddingVertical:   3,
    borderRadius:      100,
  },
  adminBadgeText: {
    fontSize:      9,
    fontWeight:    '800',
    color:         C.gold,
    letterSpacing: 1.5,
  },
  headerTitle: {
    fontSize:      15,
    fontWeight:    '800',
    color:         '#FFFFFF',
    textAlign:     'center',
    letterSpacing: -0.2,
  },
  createBtn: {
    width:           40,
    height:          40,
    borderRadius:    12,
    backgroundColor: 'rgba(255,255,255,0.18)',
    justifyContent:  'center',
    alignItems:      'center',
  },

  // ── Toast ──
  toast: {
    position:  'absolute',
    top:       Platform.OS === 'ios' ? 115 : 85,
    left:      20,
    right:     20,
    zIndex:    99,
    flexDirection: 'row',
    alignItems:    'center',
    gap:           10,
    padding:       14,
    borderRadius:  14,
  },
  toastOk:   { backgroundColor: C.green },
  toastErr:  { backgroundColor: C.red   },
  toastText: { flex: 1, color: '#fff', fontSize: 13, fontWeight: '700' },

  // ── Body ──
  body:        { flex: 1 },
  listContent: { padding: 16, paddingBottom: 60, gap: 12 },
  formContent: { padding: 18, paddingBottom: 60 },

  // ── Compteur ──
  countRow: {
    flexDirection:  'row',
    alignItems:     'center',
    gap:            6,
    marginBottom:   4,
  },
  countText: {
    fontSize:   12,
    color:      C.muted,
    fontStyle:  'italic',
  },

  // ── Loading / empty ──
  centered: {
    paddingVertical: 60,
    alignItems:      'center',
    gap:             14,
  },
  loadingText: { fontSize: 14, color: C.muted },

  empty: {
    paddingVertical: 60,
    alignItems:      'center',
    gap:             10,
  },
  emptyTitle:    { fontSize: 17, fontWeight: '700', color: C.text },
  emptySubtitle: { fontSize: 13, color: C.muted, textAlign: 'center', paddingHorizontal: 20 },
  emptyCreateBtn: {
    flexDirection:     'row',
    alignItems:        'center',
    gap:               8,
    marginTop:         10,
    paddingHorizontal: 20,
    paddingVertical:   11,
    borderRadius:      14,
    borderWidth:       1,
    borderColor:       C.purple + '60',
    backgroundColor:   C.card,
  },
  emptyCreateText: { fontSize: 14, color: C.purple, fontWeight: '700' },

  // ── Formulaire ──
  fieldLabel: {
    fontSize:      13,
    fontWeight:    '700',
    color:         C.text,
    marginBottom:  5,
  },
  fieldHint: {
    fontSize:     12,
    color:        C.muted,
    marginBottom: 10,
    lineHeight:   17,
  },
  inputWrap: {
    flexDirection:   'row',
    alignItems:      'center',
    backgroundColor: C.card,
    borderRadius:    14,
    borderWidth:     1,
    borderColor:     C.border,
    paddingHorizontal: 14,
    paddingVertical:   2,
  },
  inputWrapMulti: {
    alignItems:    'flex-start',
    paddingTop:    12,
    paddingBottom: 12,
  },
  inputIcon: { marginRight: 10 },
  input: {
    flex:          1,
    fontSize:      14,
    color:         C.text,
    paddingVertical: 12,
  },
  inputMulti: {
    minHeight:   120,
    lineHeight:  22,
  },
  charCount: {
    fontSize:   11,
    color:      C.muted,
    textAlign:  'right',
    marginTop:  4,
    marginBottom: 4,
  },

  // ── Aperçu ──
  previewWrap: {
    marginTop:    20,
    marginBottom: 20,
    gap:          10,
  },
  previewLabel: {
    flexDirection: 'row',
    alignItems:    'center',
    gap:           6,
  },
  previewLabelText: {
    fontSize:   12,
    color:      C.muted,
    fontStyle:  'italic',
  },
  previewBanner: {
    flexDirection:     'row',
    alignItems:        'center',
    backgroundColor:   'rgba(255,255,255,0.06)',
    borderWidth:       1,
    borderColor:       'rgba(255,215,0,0.25)',
    borderRadius:      14,
    paddingVertical:   9,
    paddingHorizontal: 12,
    gap:               10,
    overflow:          'hidden',
  },
  previewPill: {
    flexDirection:     'row',
    alignItems:        'center',
    gap:               5,
    backgroundColor:   'rgba(255,215,0,0.13)',
    paddingHorizontal: 9,
    paddingVertical:   4,
    borderRadius:      8,
    flexShrink:        0,
  },
  previewPillText: {
    fontSize:   10,
    fontWeight: '800',
    color:      C.gold,
  },
  previewSep: {
    width:           1,
    height:          18,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  previewTickerText: {
    flex:       1,
    fontSize:   12,
    color:      'rgba(255,255,255,0.80)',
    fontWeight: '500',
  },

  // ── Bouton save ──
  saveBtn: {
    borderRadius: 16,
    overflow:     'hidden',
    marginTop:    8,
  },
  saveBtnDisabled: { opacity: 0.45 },
  saveBtnGrad: {
    flexDirection:  'row',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            10,
    paddingVertical: 16,
  },
  saveBtnText: {
    fontSize:   15,
    fontWeight: '800',
    color:      '#fff',
    letterSpacing: 0.1,
  },

  // ── Confirmation suppression ──
  confirmOverlay: {
    flex:            1,
    backgroundColor: 'rgba(0,0,0,0.72)',
    justifyContent:  'center',
    alignItems:      'center',
    padding:         24,
  },
  confirmCard: {
    backgroundColor: C.card,
    borderRadius:    24,
    padding:         28,
    width:           Math.min(SCREEN_W - 48, 400),
    alignItems:      'center',
    borderWidth:     1,
    borderColor:     C.border,
    gap:             10,
  },
  confirmIconWrap: {
    width:           64,
    height:          64,
    borderRadius:    20,
    backgroundColor: 'rgba(239,68,68,0.12)',
    justifyContent:  'center',
    alignItems:      'center',
    marginBottom:    4,
  },
  confirmTitle: {
    fontSize:   17,
    fontWeight: '800',
    color:      C.text,
    textAlign:  'center',
  },
  confirmTitre: {
    fontSize:      13,
    color:         C.purple,
    fontWeight:    '600',
    fontStyle:     'italic',
    textAlign:     'center',
    paddingHorizontal: 10,
  },
  confirmMsg: {
    fontSize:   13,
    color:      C.muted,
    textAlign:  'center',
    lineHeight: 19,
    paddingHorizontal: 8,
  },
  confirmBtns: {
    flexDirection: 'row',
    gap:           12,
    width:         '100%',
    marginTop:     8,
  },
  confirmCancelBtn: {
    flex:           1,
    paddingVertical: 14,
    borderRadius:   14,
    borderWidth:    1,
    borderColor:    C.border,
    alignItems:     'center',
  },
  confirmCancelText: {
    fontSize:   14,
    color:      C.muted,
    fontWeight: '600',
  },
  confirmDeleteBtn: {
    flex:           1,
    paddingVertical: 14,
    borderRadius:   14,
    backgroundColor: C.red,
    alignItems:     'center',
    justifyContent: 'center',
    minHeight:      48,
  },
  confirmDeleteText: {
    fontSize:   14,
    color:      '#fff',
    fontWeight: '800',
  },
});

// ─── Styles InfoCard ──────────────────────────────────────────────────────────
const cardStyles = StyleSheet.create({
  card: {
    flexDirection:   'row',
    backgroundColor: C.card,
    borderRadius:    18,
    overflow:        'hidden',
    borderWidth:     1,
    borderColor:     C.border,
  },
  stripe: {
    width:           4,
    backgroundColor: C.purple,
  },
  body: {
    flex:    1,
    padding: 16,
    gap:     8,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems:    'center',
  },
  titlePill: {
    flexDirection:     'row',
    alignItems:        'center',
    gap:               6,
    backgroundColor:   'rgba(255,215,0,0.10)',
    paddingHorizontal: 10,
    paddingVertical:   4,
    borderRadius:      8,
    borderWidth:       1,
    borderColor:       'rgba(255,215,0,0.22)',
    flexShrink:        1,
  },
  titlePillText: {
    fontSize:      11,
    fontWeight:    '700',
    color:         C.gold,
    letterSpacing: 0.2,
  },
  texte: {
    fontSize:   13.5,
    color:      C.muted,
    lineHeight: 20,
  },
  datesRow: {
    flexDirection: 'row',
    alignItems:    'center',
    gap:           5,
  },
  dateText: {
    fontSize: 11,
    color:    C.muted,
  },
  actions: {
    flexDirection:  'row',
    gap:            10,
    marginTop:      4,
  },
  editBtn: {
    flexDirection:     'row',
    alignItems:        'center',
    gap:               6,
    paddingHorizontal: 14,
    paddingVertical:   8,
    borderRadius:      10,
    backgroundColor:   'rgba(138,43,226,0.12)',
    borderWidth:       1,
    borderColor:       C.purple + '40',
  },
  editText: {
    fontSize:   12,
    fontWeight: '700',
    color:      C.purple,
  },
  deleteBtn: {
    flexDirection:     'row',
    alignItems:        'center',
    justifyContent:    'center',
    gap:               6,
    paddingHorizontal: 14,
    paddingVertical:   8,
    borderRadius:      10,
    backgroundColor:   'rgba(239,68,68,0.10)',
    borderWidth:       1,
    borderColor:       'rgba(239,68,68,0.30)',
    minWidth:          90,
  },
  deleteText: {
    fontSize:   12,
    fontWeight: '700',
    color:      C.red,
  },
});

